import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { Divider, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import logo_black from '../../assets/images/logo_black.png';
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useAuth } from "../../api/auth";
import { useLocation } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

export default function NavigationHeader() {
    const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [logoutOpen, setLogoutOpen] = React.useState(false);
    const { logout, user } = useAuth();
    const location = useLocation();

    const handleOpenProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleCloseProfileMenu = () => {
        setProfileMenuAnchor(null);
    };

    const handleOpenLogout = () => {
        handleCloseProfileMenu();
        setLogoutOpen(true);
    };

    const handleCloseLogout = () => {
        setLogoutOpen(false);
    };

    const handleConfirmLogout = () => {
        logout();
        setLogoutOpen(false);
    };

    const initials = React.useMemo(() => {
        if (!user?.name) {
            return "?";
        }
        const parts = user.name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return "?";
        }
        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }, [user?.name]);

    const featureButtons = [
        { label: "Dashboard", needsAttention: false, url: "/" },
        { label: "Goods In", needsAttention: false, url: "/goods-in" },
        { label: "Stock", needsAttention: false, url: "/stock" },
        { label: "Stock locations", needsAttention: false, url: "/locations" },
        { label: "Products", needsAttention: false, url: "/products" },
        { label: "Relocations", needsAttention: false, url: "/relocations" },
        { label: "Audit", needsAttention: false, url: "/audit" },
    ];

    return (
        <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
                    <img src={logo_black} alt="Carron Lodge Warehouse" height={48} />
                </Box>
                <Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {featureButtons.map((feature) => {
                            const isActive = feature.url === "/"
                                ? location.pathname === "/"
                                : location.pathname.startsWith(feature.url);
                            return (
                                <Button
                                    key={feature.label}
                                    variant={isActive ? "contained" : "text"}
                                    href={feature.url}
                                    color={feature.needsAttention ? "warning" : "primary"}
                                    endIcon={
                                        feature.needsAttention ? (
                                            <WarningAmberRoundedIcon
                                                fontSize="small"
                                            />
                                        ) : undefined
                                    }
                                >
                                    {feature.label}
                                </Button>
                            );
                        })}
                    </Stack>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
                    <IconButton onClick={handleOpenProfileMenu} size="small" aria-label="Open profile menu">
                        <Avatar sx={{ width: 42, height: 42, fontWeight: 600 }}>
                            {initials}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={profileMenuAnchor}
                        open={Boolean(profileMenuAnchor)}
                        onClose={handleCloseProfileMenu}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MenuItem onClick={handleOpenLogout}>
                            <LogoutRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                            Log out
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
            <Divider />

            <Dialog open={logoutOpen} onClose={handleCloseLogout} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm logout</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to sign out? You will need to log in again to continue.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLogout} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmLogout} color="error" variant="contained">
                        Log out
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};